import {
  ActivityNode,
  ActivityPlanningActions,
  ActivityPlanningCards,
  ActivityPlanningContextModel,
  ActivityPlanningSummary,
  ActivityPlanningWorkbenchModel,
  IndicatorCandidate,
  KpiPlanRow,
} from './activityplanning.models';

const n = (value:any, fallback=0) =>
  Number.isFinite(Number(value)) ? Number(value) : fallback;

const s = (value:any, fallback='') =>
  value === null || value === undefined ? fallback : String(value);

const b = (value:any) =>
  value === true ||
  value === 1 ||
  value === '1' ||
  String(value).toLowerCase() === 'true';

export const formatNumber = (value:any) =>
  n(value).toLocaleString();

export const formatMoney = (value:any) => {
  const valueNumber = n(value);
  const abs = Math.abs(valueNumber);

  if (abs >= 1_000_000_000_000)
    return `${(valueNumber / 1_000_000_000_000).toFixed(2)} T`;

  if (abs >= 1_000_000_000)
    return `${(valueNumber / 1_000_000_000).toFixed(2)} B`;

  if (abs >= 1_000_000)
    return `${(valueNumber / 1_000_000).toFixed(2)} M`;

  return valueNumber.toLocaleString();
};

export const formatPercent = (value:any) => {
  if (value === null || value === undefined || value === '')
    return '-';

  return `${n(value).toFixed(2)}%`;
};

export const normalizePlanningContext = (
  raw:any,
):ActivityPlanningContextModel => ({
  planningcycleid:n(raw?.planningcycleid),
  userid:n(raw?.userid),
  profileid:raw?.profileid == null ? null : n(raw.profileid),
  profilestamp:raw?.profilestamp ?? null,

  cci:n(raw?.cci),
  ccc:s(raw?.ccc),
  ccn:s(raw?.ccn),

  costcenters:Array.isArray(raw?.costcenters)
    ?raw.costcenters.map((item:any)=>({
        staffuserid:n(item?.staffuserid),
        staffid:n(item?.staffid),
        staffcode:s(item?.staffcode),
        staffname:s(item?.staffname),
        unitid:n(item?.unitid),
        unitcode:s(item?.unitcode),
        unitname:s(item?.unitname),
        id:n(item?.id),
        code:s(item?.code),
        name:s(item?.name),
        primary:b(item?.primary),
        delegated:b(item?.delegated),
        typecode:s(item?.typecode),
        statecode:s(item?.statecode),
      }))
    :[],

  multiplecostcenters:b(raw?.multiplecostcenters),
  sudo:b(raw?.sudo),
});

export const normalizePlanningSummary = (
  raw:any,
):ActivityPlanningSummary => ({
  indicatorcount:n(raw?.indicatorcount),
  activitycount:n(raw?.activitycount),
  taskcount:n(raw?.taskcount),
  budgetlinecount:n(raw?.budgetlinecount),
  budgetactivitycount:n(raw?.budgetactivitycount),
  plannedamount:n(raw?.plannedamount),
});

export const normalizePlanningActions = (
  raw:any,
):ActivityPlanningActions => ({
  canRefresh:b(raw?.canRefresh),
  canChangeCostCenter:b(raw?.canChangeCostCenter),
  canAddIndicator:b(raw?.canAddIndicator),
  canAddActivity:b(raw?.canAddActivity),
  canAddTask:b(raw?.canAddTask),
  canApplyTemplate:b(raw?.canApplyTemplate),
  canSubmit:b(raw?.canSubmit),
  canExit:b(raw?.canExit),
  nextpage:raw?.nextpage ?? null,
});

export const normalizeActivityPlanningWorkbench = (
  raw:any,
):ActivityPlanningWorkbenchModel => {
  const source = raw?.sd ?? raw ?? {};

  return {
    context:normalizePlanningContext(source.context),
    summary:normalizePlanningSummary(source.summary),
    tree:Array.isArray(source.tree) ? source.tree : [],
    actions:normalizePlanningActions(source.actions),
  };
};

export const normalizeIndicatorCandidate = (
  raw:any,
):IndicatorCandidate => ({
  rid:n(raw?.rid),
  rix:s(raw?.rix),
  shc:s(raw?.shc),
  nam:s(raw?.nam),
  dsc:s(raw?.dsc),

  typeid:n(raw?.typeid),
  frequencyid:n(raw?.frequencyid),
  frequencycode:s(raw?.frequencycode),
  frequencyname:s(raw?.frequencyname),

  uomid:n(raw?.uomid),
  uomcode:s(raw?.uomcode),
  uomname:s(raw?.uomname),
  uomsymbol:s(raw?.uomsymbol),
  uomminimum:
    raw?.uomminimum == null ? null : n(raw.uomminimum),
  uommaximum:
    raw?.uommaximum == null ? null : n(raw.uommaximum),
  uomstep:
    raw?.uomstep == null ? null : n(raw.uomstep),
  uombounded:b(raw?.uombounded),

  targetdescription:s(raw?.targetdescription),
  recommendedbaseline:
    raw?.recommendedbaseline == null
      ?null
      :n(raw.recommendedbaseline),
  recommendedwarninglow:
    raw?.recommendedwarninglow == null
      ?null
      :n(raw.recommendedwarninglow),
  recommendedwarninghigh:
    raw?.recommendedwarninghigh == null
      ?null
      :n(raw.recommendedwarninghigh),

  objectiveid:n(raw?.objectiveid),
  objectivecode:s(raw?.objectivecode),
  objectivename:s(raw?.objectivename),

  axisid:n(raw?.axisid),
  axiscode:s(raw?.axiscode),
  axisname:s(raw?.axisname),

  formula:s(raw?.formula),
  interpretation:s(raw?.interpretation),
  suggestedtarget:n(raw?.suggestedtarget),
});

export const normalizeKpiPlanRows = (
  model:ActivityPlanningWorkbenchModel,
):KpiPlanRow[] => {
  const rows = new Map<number,KpiPlanRow>();

  for (const raw of model.tree ?? []) {
    const kpiplanid=n(raw?.kpiplanid);

    if(!kpiplanid || rows.has(kpiplanid))
      continue;

    rows.set(kpiplanid,{
      id:kpiplanid,
      kpiplanid,
      kpiplanix:s(raw?.kpiplanix),
      kpiid:n(raw?.kpiid),

      kpicode:s(raw?.kpicode),
      kpiname:s(raw?.kpiname),
      kpidescription:s(raw?.kpidescription),

      uomcode:s(raw?.uomcode),
      uomname:s(raw?.uomname),
      uomsymbol:s(raw?.uomsymbol),
      frequencycode:s(raw?.frequencycode),
      frequencyname:s(raw?.frequencyname),

      axiscode:s(raw?.axiscode),
      axisname:s(raw?.axisname),
      objectivecode:s(raw?.objectivecode),
      objectivename:s(raw?.objectivename),

      baselinevalue:n(raw?.baselinevalue),
      targetvalue:n(raw?.targetvalue),
      thresholdmin:
        raw?.thresholdmin == null ? null : n(raw.thresholdmin),
      thresholdmax:
        raw?.thresholdmax == null ? null : n(raw.thresholdmax),
      description:s(raw?.kpiplandescription),

      formula:s(raw?.formula),
      interpretation:s(raw?.interpretation),

      statecode:s(raw?.statecode,'DRAFT'),
      statename:s(raw?.statename,'Draft'),
      stamp:s(raw?.kpiplanstamp),

      _state:'clean',
      __dirty:false,
      __error:'',
      raw,
    });
  }

  return Array.from(rows.values());
};

export const normalizeWorkbenchTree = (
  model:ActivityPlanningWorkbenchModel,
):ActivityNode[] => {
  const output:ActivityNode[]=[];
  const indicators=new Set<string>();
  const activities=new Set<string>();
  const tasks=new Set<string>();

  for(const row of model.tree ?? []){
    const kpiplanid=n(row?.kpiplanid);
    const activityplanid=n(row?.activityplanid);
    const taskplanid=n(row?.taskplanid);

    const kpiNid=`KPI-${kpiplanid}`;
    const activityNid=`ACTIVITY-${activityplanid}`;
    const taskNid=`TASK-${taskplanid}`;

    if(kpiplanid&&!indicators.has(kpiNid)){
      indicators.add(kpiNid);

      output.push({
        nid:kpiNid,
        pid:null,
        rid:kpiplanid,
        kpiplanid,
        kpiid:n(row?.kpiid),

        code:s(row?.kpicode),
        shortcode:s(row?.kpicode),
        name:s(row?.kpiname),
        recname:s(row?.kpiname),
        description:s(row?.kpiplandescription||row?.kpidescription),

        nodetype:'KPI',
        levelno:1,

        axiscode:s(row?.axiscode),
        axisname:s(row?.axisname),
        objectivecode:s(row?.objectivecode),
        objectivename:s(row?.objectivename),

        costcenterid:n(row?.costcenterid),
        costcentercode:s(row?.costcentercode||model.context.ccc),
        costcentername:s(row?.costcentername||model.context.ccn),

        status:s(row?.statecode,'DRAFT'),
        totalamount:n(row?.activityplannedamount),
        raw:row,
      });
    }

    if(activityplanid&&!activities.has(activityNid)){
      activities.add(activityNid);

      output.push({
        nid:activityNid,
        pid:kpiNid,
        rid:activityplanid,
        prid:kpiplanid,

        kpiplanid,
        kpiid:n(row?.kpiid),

        activityplanid,
        activityid:n(row?.activityid),

        code:s(row?.activitycode),
        shortcode:s(row?.activitycode),
        name:s(row?.activityname),
        recname:s(row?.activityname),

        nodetype:'ACTIVITY',
        levelno:2,

        axiscode:s(row?.axiscode),
        axisname:s(row?.axisname),
        objectivecode:s(row?.objectivecode),
        objectivename:s(row?.objectivename),

        costcenterid:n(row?.costcenterid),
        costcentercode:s(row?.costcentercode),
        costcentername:s(row?.costcentername),

        contributionweight:n(row?.contributionweight),
        status:s(row?.statecode),
        totalamount:n(row?.activityplannedamount),
        budgetlinecount:n(row?.budgetlinecount),
        budgetstate:n(row?.activitybudgetstate),
        raw:row,
      });
    }

    if(taskplanid&&!tasks.has(taskNid)){
      tasks.add(taskNid);

      output.push({
        nid:taskNid,
        pid:activityNid,
        rid:taskplanid,
        prid:activityplanid,

        kpiplanid,
        kpiid:n(row?.kpiid),

        activityplanid,
        activityid:n(row?.activityid),

        taskplanid,
        taskid:n(row?.taskid),

        code:s(row?.taskcode),
        shortcode:s(row?.taskcode),
        name:s(row?.taskname),
        recname:s(row?.taskname),

        nodetype:'TASK',
        levelno:3,

        axiscode:s(row?.axiscode),
        axisname:s(row?.axisname),
        objectivecode:s(row?.objectivecode),
        objectivename:s(row?.objectivename),

        costcenterid:n(row?.costcenterid),
        costcentercode:s(row?.costcentercode),
        costcentername:s(row?.costcentername),

        taskweight:n(row?.taskweight),
        status:s(row?.statecode),
        totalamount:n(row?.taskplannedamount),
        budgetlinecount:n(row?.budgetlinecount),
        budgetstate:n(row?.taskbudgetstate),
        raw:row,
      });
    }
  }

  return output;
};

export const summaryCards = (
  model?:ActivityPlanningWorkbenchModel,
  selected?:ActivityNode|null,
):ActivityPlanningCards|null => {
  if(!model) return null;

  return {
    activitytotal:model.summary.activitycount,
    tasktotal:model.summary.taskcount,
    budgettotal:n(selected?.totalamount??model.summary.plannedamount),
    variance:0,
    weightedprogress:0,

    taskcount:selected?.nodetype==='ACTIVITY'
      ?normalizeWorkbenchTree(model).filter(
          row=>
            row.nodetype==='TASK'
            &&Number(row.activityplanid)===Number(selected.activityplanid),
        ).length
      :model.summary.taskcount,

    budgetlinecount:
      selected?.budgetlinecount
      ??model.summary.budgetlinecount,
  };
};
